import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { handleRequest } from '../../../utils/httpRequest';
import { getEndpointUrl } from '../../../utils/endPoints';
import DealerSearch from './DealerSearch/DealerSearch';
import DealerMap from './DealerMap/DealerMap';
import generalController from '../../../controllers/generalController';
import { capitalCities, localStorageKeys } from '../../../utils/constants';
import formValidationController from '../../../controllers/formValidationController';
import { convertPostalToCoordinates, getCurrentBounds, getDealersInViewport, getPreviousUserlocation, getUserGeoLocation, storeUserLocation } from '../../../utils/googleMapHelper';
import { languageController } from '../../../controllers/language/languageController';
import parse from 'html-react-parser';
import { getItemOnLocalStorage } from '../../../utils/helpers';

function FindADealer({
  isEVonly,
  isStepRenderType,
  isEmbeddedOnBnP,
  onSelectDealer,
  formExtraParams
}) {
  const [dealers, setDealers] = useState([])
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [map, setMap] = useState(null)
  const [statusMessage, setStatusMessage] = useState({ text: '', isError: false })
  const [errorMessage, setErrorMessage] = useState('')
  const [initKeyword, setInitKeyword] = useState(null)

  const { introductionText, BaSALink, BaTDLink, GaQLink } = formExtraParams

  const searchDealers = async (searchValue, searchType) => {
    if (!searchValue) return;

    if (errorMessage) {
      setErrorMessage('')
    }

    let dealerApi = ''
    let newBounds = null

    // For keyword search (dealer name or city name)
    if (searchType === 'keyword') {
      dealerApi = `${getEndpointUrl('find-dealers')}&keyword=${searchValue}`;
    }
    // For geolocation search (lat and long)
    else if (searchType === 'geolocation') {
      // Set the passed coordinates value as center.
      map.setCenter({
        lat: searchValue.lat,
        lng: searchValue.lng
      })
      // Get new bounds after setCenter
      newBounds = getCurrentBounds(map)
      // Get a dealer api url with some params.
      // Pass 'newBounds' to api to limit dealers based on min/max lat and long.
      dealerApi = `${getEndpointUrl('find-dealers')}&lat=${searchValue.lat}&lng=${searchValue.lng}&minlng=${newBounds.minLong}&maxlng=${newBounds.maxLong}&minlat=${newBounds.minLat}&maxlat=${newBounds.maxLat}`;
    }

    if (isEVonly) {
      dealerApi = `${dealerApi}&ev_only=true`;
    }

    try {
      const res = await handleRequest('get', dealerApi);
      const dealerData = res.data;

      // Check if res includes dealers data.
      if (dealerData.length > 0) {

        if (searchType === 'keyword') {
          // Set the first most relavant dealer as center.
          map.setCenter({
            lat: dealerData[0].geolocation.latitude,
            lng: dealerData[0].geolocation.longitude
          })
          // Get new bounds after setCenter
          newBounds = getCurrentBounds(map)
          // Set only dealers within the bounds/viewport.
          setDealers(getDealersInViewport(dealerData, newBounds))
        } else {
          setDealers(dealerData)
        }
      } else {
        setErrorMessage(languageController.getCopy('findDealer', 'errorNoResults'))
      }
    } catch (err) {
      console.log(err)
      setErrorMessage(languageController.getCopy('findDealer', 'errorNoResults'))
      setDealers([])
    }
  }

  // Search dealers based on entered search value.
  const onDealerSearchSubmit = async (searchValue) => {
    if (!searchValue) return;
  
    setStatusMessage({ text: languageController.getCopy('findDealer', 'searching'), isError: false })

    // Check if it is a postalcode search
    if (formValidationController.validateField(searchValue, 'postalcode')) {
      const coordinates = await convertPostalToCoordinates(searchValue)
      await searchDealers(coordinates, 'geolocation')
      // Store user postalcode data in localstorage
      storeUserLocation('postalcode', { postalcode: searchValue, coordinates: coordinates })
    }
    // Otherwise, perform a keyword search
    else {
      await searchDealers(searchValue, 'keyword')
    }
    setStatusMessage({ isError: false, text: '' })
  }

  // Search dealers based on user's geolocation.
  const onGeolocationSubmit = async () => {
    setStatusMessage({ text: languageController.getCopy('findDealer', 'geoLoading'), isError: false })

    const res = await getUserGeoLocation();

    if (res.success) {
      await searchDealers(res.data, 'geolocation')
      setStatusMessage({ isError: false, text: '' })
      // Set user geolocation in localstorage.
      storeUserLocation('geolocation', res.data)
    } else {
      setStatusMessage({ text: res.data, isError: true })
      // Remove error message after 3s
      setTimeout(() => {
        setStatusMessage({ isError: false, text: '' })
      }, 3000)
    }
  }

  useEffect(() => {
    // Load dealers based on what info is available after map gets loaded.
    async function handleInitLoad() {
      setStatusMessage({ text: languageController.getCopy('findDealer', 'searching'), isError: false })

      // Get previously set dealer data if exists.
      const prevDealer = getItemOnLocalStorage(localStorageKeys.DEALER_SELECTED_BY_USER);
      // Get previously set user location data if exists.
      const prevUserLocationData = getPreviousUserlocation();

      // If it is a step render type AND dealer was chosen before on the site.
      if (isStepRenderType && prevDealer) {
        await searchDealers({ lat: prevDealer.geolocation.latitude, lng: prevDealer.geolocation.longitude }, 'geolocation')
      }
      // If user location already exists in localstorage (geolocation or postalcode)
      else if (prevUserLocationData) {
        // Geolocation
        if (prevUserLocationData.type === 'geolocation') {
          await searchDealers(prevUserLocationData.value, 'geolocation')
        }
        // Postalcode
        else if (prevUserLocationData.type === 'postalcode') {
          const { coordinates, postalcode } = prevUserLocationData.value;
          await searchDealers(coordinates, 'geolocation')
          // Set postalcode as an init input value.
          setInitKeyword(postalcode)
        }
      }
      // Otherwise, use capital city of user's province
      else {
        const capitalData = capitalCities[generalController.getMetatagValue('province')];
        if (capitalData) {
          await searchDealers(capitalData.location, 'geolocation')
        }
      }

      setStatusMessage({ isError: false, text: '' })
    }

    if (map) {
      handleInitLoad()
    }
  }, [map])

  return (
    <div className="mz-find-a-dealer" data-mz-form-type="FindADealer">
      {introductionText && (
        <div className="mz-find-a-dealer__intro-text mz-container">
          <div className="mz-full-width">
            {parse(introductionText)}
          </div>
        </div>
      )}
      <DealerSearch
        initKeyword={initKeyword}
        onSubmit={onDealerSearchSubmit}
        onGeolocationSubmit={() => onGeolocationSubmit()}
        statusMessage={statusMessage}
        errorMessage={errorMessage}
      />
      <DealerMap
        dealers={dealers}
        searchDealers={searchDealers}
        isMapLoaded={isMapLoaded}
        setMap={setMap}
        map={map}
        setIsMapLoaded={setIsMapLoaded}
        isStepRenderType={isStepRenderType}
        isEmbeddedOnBnP={isEmbeddedOnBnP}
        onSelectDealer={onSelectDealer}
        BaSALink={BaSALink}
        BaTDLink={BaTDLink}
        GaQLink={GaQLink}
        currentLang={generalController.detectLanguage()}
      />
    </div>
  )
}

FindADealer.propTypes = {
  isEVonly: PropTypes.bool,
  isStepRenderType: PropTypes.bool,
  isEmbeddedOnBnP: PropTypes.bool,
  onSelectDealer: PropTypes.func,
  formExtraParams: PropTypes.object,
}

FindADealer.defaultProps = {
  isEVonly: false,
  isStepRenderType: false,
  isEmbeddedOnBnP: false,
  onSelectDealer: undefined,
  formExtraParams: {}
}

export default FindADealer